/**
 * Taxes Tab
 *
 * Displays contract tax configuration
 */

import { Loader2, Receipt } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { useContractTaxes } from '../../hooks/useContracts'

interface TaxesTabProps {
  contractId: string
}

export function TaxesTab({ contractId }: TaxesTabProps) {
  const { data: taxes, isLoading, error } = useContractTaxes(contractId)

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <Loader2 className='h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground' />
          <p className='text-muted-foreground'>Loading tax configuration...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <p className='text-red-600 font-medium'>Failed to load taxes</p>
          <p className='text-sm text-muted-foreground mt-1'>{error.message}</p>
        </div>
      </div>
    )
  }

  if (!taxes || taxes.length === 0) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <Receipt className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
          <p className='text-muted-foreground font-medium'>
            No tax configuration
          </p>
          <p className='text-sm text-muted-foreground mt-1'>
            This contract has no custom tax configuration
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {taxes.map(tax => (
        <div key={tax.id} className='border rounded-lg p-4 space-y-3'>
          <div className='flex items-start justify-between'>
            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <h4 className='font-medium'>{tax.name}</h4>
                <Badge variant='outline'>{tax.rate ?? 0}%</Badge>
              </div>
              {tax.description && (
                <p className='text-sm text-muted-foreground'>
                  {tax.description}
                </p>
              )}
            </div>
          </div>

          {/* Tax details */}
          <div className='grid grid-cols-2 gap-3 text-sm'>
            {tax.jurisdiction && (
              <div>
                <span className='text-muted-foreground'>Jurisdiction:</span>{' '}
                <span className='font-medium'>{tax.jurisdiction}</span>
              </div>
            )}
            {tax.tax_type && (
              <div>
                <span className='text-muted-foreground'>Type:</span>{' '}
                <span className='font-medium capitalize'>{tax.tax_type}</span>
              </div>
            )}
            {tax.applies_to && (
              <div>
                <span className='text-muted-foreground'>Applies To:</span>{' '}
                <span className='font-medium capitalize'>{tax.applies_to}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
